// ============================================================
//  GlossaryPanel.jsx — Master Glossary A1→C2  |  Spotify UX v2
//  v2.2 — Difficulty Filter: 🟢 Basic · 🟡 Intermediate · 🔴 Advanced
//  v2.3 — Fusión Neon: datos dinámicos + estáticos (bypass)
// ============================================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './GlossaryPanel.css';
import {
  TENSES, SPECIALS, SECTIONS, PH,
  COLL_DATA, SYNTAX_DATA, PRON_DATA,
  SPEAKING_DATA, PRAGM_DATA, THEMATIC_DATA,
  LEVEL_MAP,
} from './glossaryData';

import MiniGameModal from './MiniGameModal';

// 🚀 NUEVO: Importamos el Hook de Neon
import { useGlossaryData } from '../../hooks/useGlossaryData';
// ─────────────────────────────────────────────
//  CONFIG
// ─────────────────────────────────────────────
const PAGE_SIZE = 12;

// ── Difficulty levels ──
const DIFFICULTY = {
  basic:        { label: 'Basic',        hint: 'A1–A2', emoji: '🟢', color: '#22c55e' },
  intermediate: { label: 'Intermediate', hint: 'B1–B2', emoji: '🟡', color: '#f59e0b' },
  advanced:     { label: 'Advanced',     hint: 'C1–C2', emoji: '🔴', color: '#ef4444' },
};

// Helper — get difficulty level for a word object
function getWordLevel(w) {
  if (!w?.en) return null;
  return LEVEL_MAP[w.en.toLowerCase()] || null;
}

// ── Emoji map for subsection cards ──
const SUB_EMOJI = {
  'Common Nouns':'🏷️','Abstract Nouns':'💭','Countable Nouns':'🔢',
  'Uncountable Nouns':'♾️','Collective Nouns':'👥','Irregular Plurals':'⚠️',
  'Regular Verbs':'✅','Irregular Verbs':'⚡','Action / Dynamic Verbs':'🏃',
  'State / Stative Verbs':'🧘','Auxiliary Verbs':'🔧','Modal Verbs':'🎯',
  'Subject Pronouns':'👤','Object Pronouns':'🫵',
  'Possessive Adjectives & Pronouns':'🏠','Reflexive Pronouns':'🪞',
  'Demonstrative Pronouns':'👉','Indefinite & Relative Pronouns':'❓',
  'Descriptive (before noun)':'🎨','Predicative (after verb)':'💬',
  'Quantitative':'🔢','Comparative & Superlative':'📊',
  'Time · Place · Manner':'⏰','Frequency':'🔁','Degree · Hedging':'📏',
  'Time: IN · ON · AT':'🕐','Place · Movement':'📍','Prepositional Phrases':'🔗',
  'Familia GET':'💪','Familia TAKE · SET':'🎯','Familia LOOK · BREAK':'👀',
  'Familia COME · RUN':'🏃','FANBOYS — Coordinating':'🔗',
  'Subordinating Conjunctions':'🌿','Discourse Markers':'📝',
  'Survival Phrases — Real Life':'🆘','Business & Meeting Idioms':'💼',
  'Conversational Fillers & Linkers':'🗣️','MAKE vs DO':'⚔️',
  'HAVE vs TAKE':'🤲','Strong Verb Collocations':'💪',
  'Power Adjective + Noun':'⚡','Business & Tech Idioms Fijos':'🏆',
  '🔗 Connected Speech':'🔗','🗣️ Vowel Sounds':'🗣️',
  '👂 Minimal Pairs':'👂','🤫 Silent Letters':'🤫','🎯 Word Stress':'🎯',
  '⏳ Buying Time':'⏳','🔄 Paraphrasing':'🔄',
  '✋ Interrupting Politely':'✋','🤝 Agreeing & Disagreeing':'🤝',
  '🌫️ Hedging':'🌫️','🎯 Conversation Continuers':'🎙️',
  '🇬🇧 British Understatement':'🇬🇧','📊 Register Ladder':'📊',
  '🤝 Face-Saving Language':'🫱','☕ Small Talk':'☕',
};

function extractLeadingEmoji(str) {
  const m = str.match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/u);
  return m ? m[0] : null;
}
function stripLeadingEmoji(str) {
  return str.replace(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)\s*/u, '');
}

const SECTION_META = {
  tenses:       { icon:'⏳',  label:'Verb Tenses',   color:'#f59e0b', count:'13 tenses',   group:'Core Grammar', desc:'Complete matrix A1→C2' },
  nouns:        { icon:'🏷️', label:'Nouns',          color:'#3b82f6', count:'~300 words',  group:'Core Grammar', desc:'Common · Abstract · Irregular' },
  verbs:        { icon:'⚡',  label:'Verbs',          color:'#22c55e', count:'~300 words',  group:'Core Grammar', desc:'Regular · Irregular · Modal' },
  pronouns:     { icon:'👤', label:'Pronouns',       color:'#f43f5e', count:'~80 words',   group:'Core Grammar', desc:'Subject · Object · Reflexive' },
  adjectives:   { icon:'🎨', label:'Adjectives',     color:'#f59e0b', count:'~150 words',  group:'Core Grammar', desc:'Descriptive · Comparative' },
  adverbs:      { icon:'🔄', label:'Adverbs',        color:'#a855f7', count:'~120 words',  group:'Core Grammar', desc:'Time · Manner · Degree' },
  prepositions: { icon:'📍', label:'Prepositions',   color:'#f97316', count:'~80 words',   group:'Core Grammar', desc:'IN/ON/AT · Place · Phrases' },
  phrasalVerbs: { icon:'🔀', label:'Phrasal Verbs',  color:'#0ea5e9', count:'~600 words',  group:'Core Grammar', desc:'Daily Life · Tech · Biz · Social' },
  conjunctions: { icon:'🔗', label:'Conjunctions',   color:'#ec4899', count:'~140 words',   group:'Core Grammar', desc:'Linking Words · Transitions' },
  functional:   { icon:'💬', label:'Functional',     color:'#84cc16', count:'~230 phrases',group:'Core Grammar', desc:'Survival · Business · Small Talk' },
  collocations: { icon:'🧩', label:'Collocations',   color:'#f59e0b', count:'~700 items',  group:'⚡ God Tier',  desc:'MAKE/DO · HAVE/TAKE · Idioms' },
  syntax:       { icon:'🏗️', label:'Adv. Syntax',   color:'#c084fc', count:'~40 structs', group:'⚡ God Tier',  desc:'Inversion · Cleft · C1/C2' },
  pronunciation:{ icon:'🔊', label:'Pronunciation',  color:'#fb7185', count:'~80 items',   group:'⚡ God Tier',  desc:'IPA · Minimal Pairs · Stress' },
  speaking:     { icon:'🎙️', label:'Speaking',       color:'#34d399', count:'~80 phrases', group:'⚡ God Tier',  desc:'Fluency · Hedging · Polite' },
  pragmatics:   { icon:'🌍', label:'Pragmatics',     color:'#60a5fa', count:'~60 items',   group:'⚡ God Tier',  desc:'British · Register · Culture' },
  thematic:     { icon:'🏠', label:'Temático',       color:'#fbbf24', count:'~400 words',  group:'🏠 Temático',  desc:'Casa · Viajes · Familia · +10' },
};

const GROUPS = ['Core Grammar', '⚡ God Tier', '🏠 Temático'];

// ─────────────────────────────────────────────
//  UTILS
// ─────────────────────────────────────────────
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <span key={i} className="hi">{part}</span>
          : part
      )}
    </>
  );
}

// ─────────────────────────────────────────────
//  ★ DIFFICULTY FILTER  (new v2.2)
// ─────────────────────────────────────────────
function DifficultyFilter({ words, activeDiff, onSelect }) {
  const counts = { basic: 0, intermediate: 0, advanced: 0 };
  words.forEach(w => {
    const lvl = getWordLevel(w);
    if (lvl) counts[lvl]++;
  });
  const totalTagged = counts.basic + counts.intermediate + counts.advanced;

  // Hide entirely if no words in this pool have level tags
  if (totalTagged === 0) return null;

  return (
    <div className="difficulty-row">
      <span className="difficulty-row-label">Level</span>

      {/* All */}
      <button
        className={`diff-btn ${activeDiff === 'all' ? 'diff-active' : ''}`}
        style={{ '--diff-color': 'var(--tx)' }}
        onClick={() => onSelect('all')}
      >
        <span className="diff-emoji">📚</span>
        <span className="diff-label">All</span>
        <span className="diff-count">{words.length}</span>
      </button>

      {/* Basic · Intermediate · Advanced */}
      {Object.entries(DIFFICULTY).map(([key, cfg]) =>
        counts[key] > 0 ? (
          <button
            key={key}
            className={`diff-btn ${activeDiff === key ? 'diff-active' : ''}`}
            style={{ '--diff-color': cfg.color }}
            onClick={() => onSelect(activeDiff === key ? 'all' : key)}
            title={`${cfg.label} — ${cfg.hint}`}
          >
            <span className="diff-emoji">{cfg.emoji}</span>
            <span className="diff-label">{cfg.label}</span>
            <span className="diff-sub">{cfg.hint}</span>
            <span className="diff-count">{counts[key]}</span>
          </button>
        ) : null
      )}
    </div>
  );
}

// Empty state when level filter has 0 results
function DifficultyEmpty({ level }) {
  const cfg = DIFFICULTY[level];
  return (
    <div className="diff-empty">
      <div className="diff-empty-icon">{cfg?.emoji || '🔍'}</div>
      <div className="diff-empty-text">No {cfg?.label} words in this category</div>
      <div className="diff-empty-sub">Try a different level or subgroup</div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  WORD CARDS  (with optional level badge)
// ─────────────────────────────────────────────
function WordCard({ w, color, query, studyMode, onCopy, view = 'grid', index, showLevelBadge }) {
  const [revealed, setRevealed] = useState(false);
  const ph = w.ph || PH[w.en?.toLowerCase()] || '';
  const level = getWordLevel(w);
  const lvlCfg = level ? DIFFICULTY[level] : null;
  const hideES = studyMode === 'study-es';
  const hideEN = studyMode === 'study-en';
  const isStudying = studyMode !== 'normal';

  const handleClick = () => { if (isStudying) setRevealed(r => !r); else onCopy(w.en); };
  useEffect(() => { setRevealed(false); }, [studyMode]);

  if (view === 'list') {
    return (
      <div
        className={`wrow-new ${isStudying ? 'studying' : ''} ${revealed ? 'revealed' : ''}`}
        onClick={handleClick}
        style={{ '--accent': color, animationDelay: `${index * 0.03}s` }}
      >
        <span className="wrow-num">{String(index + 1).padStart(2, '0')}</span>
        <span className={`wrow-en ${hideEN && !revealed ? 'blurred' : ''}`}>
          <Highlight text={w.en} query={query} />
          {ph && <span className="wrow-ph">{ph}</span>}
        </span>
        <span className={`wrow-es ${hideES && !revealed ? 'blurred' : ''}`} style={{ color }}>{w.es}</span>
        {w.note && <span className="wrow-note">{w.note}</span>}
        {isStudying && <span className="reveal-badge">{revealed ? '✓' : '👁'}</span>}
      </div>
    );
  }

  return (
    <div
      className={`wcard-new ${isStudying ? 'studying' : ''} ${revealed ? 'revealed' : ''}`}
      onClick={handleClick}
      style={{ '--accent': color, animationDelay: `${index * 0.04}s` }}
    >
      {/* Level badge visible in "All" view so user can see the mix */}
      {showLevelBadge && lvlCfg && (
        <span
          className="wcard-level-badge"
          style={{
            background: `color-mix(in srgb,${lvlCfg.color} 18%,var(--bg))`,
            color: lvlCfg.color,
          }}
        >
          {lvlCfg.emoji} {lvlCfg.label}
        </span>
      )}
      <div className={`study-field ${hideEN && !revealed ? 'field-hidden' : ''}`}>
        <div className="wcard-en"><Highlight text={w.en} query={query} /></div>
        {ph && <div className="wcard-ph">{ph}</div>}
        {hideEN && !revealed && <span className="field-hint">👁 tap to reveal</span>}
      </div>
      <div className={`study-field ${hideES && !revealed ? 'field-hidden' : ''}`}>
        <div className="wcard-es" style={{ color }}>{w.es}</div>
        {hideES && !revealed && <span className="field-hint">👁 tap</span>}
      </div>
      {w.note && <div className="wcard-note">{w.note}</div>}
      {isStudying && revealed && <div className="revealed-check">✓</div>}
    </div>
  );
}

function IrregularWordCard({ w, color, query, studyMode, onCopy, view, index, showLevelBadge }) {
  const [revealed, setRevealed] = useState(false);
  const isStudying = studyMode !== 'normal';
  const hideES = studyMode === 'study-es';
  const hideEN = studyMode === 'study-en';
  const level = getWordLevel(w);
  const lvlCfg = level ? DIFFICULTY[level] : null;

  const handleClick = () => { if (isStudying) setRevealed(r => !r); else onCopy(w.en); };
  useEffect(() => { setRevealed(false); }, [studyMode]);

  if (view === 'list') {
    return (
      <div
        className={`wrow-new irr-row ${isStudying ? 'studying' : ''} ${revealed ? 'revealed' : ''}`}
        onClick={handleClick}
        style={{ '--accent': color, animationDelay: `${index * 0.03}s` }}
      >
        <span className="wrow-num">{String(index + 1).padStart(2, '0')}</span>
        <span className={`wrow-en ${hideEN && !revealed ? 'blurred' : ''}`}><Highlight text={w.en} query={query} /></span>
        <span className={`irr-past ${hideEN && !revealed ? 'blurred' : ''}`} style={{ color }}>{w.past}</span>
        <span className={`irr-pp ${hideEN && !revealed ? 'blurred' : ''}`} style={{ color: color + 'bb' }}>{w.pp}</span>
        <span className={`wrow-es ${hideES && !revealed ? 'blurred' : ''}`} style={{ color: 'var(--tx2)' }}>{w.es}</span>
        {isStudying && <span className="reveal-badge">{revealed ? '✓' : '👁'}</span>}
      </div>
    );
  }

  return (
    <div
      className={`wcard-new ${isStudying ? 'studying' : ''} ${revealed ? 'revealed' : ''}`}
      onClick={handleClick}
      style={{ '--accent': color, animationDelay: `${index * 0.04}s` }}
    >
      {showLevelBadge && lvlCfg && (
        <span
          className="wcard-level-badge"
          style={{ background:`color-mix(in srgb,${lvlCfg.color} 18%,var(--bg))`, color:lvlCfg.color }}
        >
          {lvlCfg.emoji} {lvlCfg.label}
        </span>
      )}
      <div className={`study-field ${hideEN && !revealed ? 'field-hidden' : ''}`}>
        <div className="wcard-en"><Highlight text={w.en} query={query} /></div>
        <div className="irr-forms-mini">
          <span className="irr-tag">PAST</span>
          <span style={{ color, fontWeight:700 }}>{w.past}</span>
          <span className="irr-tag">P.P.</span>
          <span style={{ color, fontWeight:700 }}>{w.pp}</span>
        </div>
        {hideEN && !revealed && <span className="field-hint">👁 tap to reveal</span>}
      </div>
      <div className={`study-field ${hideES && !revealed ? 'field-hidden' : ''}`}>
        <div className="wcard-es" style={{ color }}>{w.es}</div>
        {hideES && !revealed && <span className="field-hint">👁 tap</span>}
      </div>
      {w.note && <div className="wcard-note">{w.note}</div>}
      {isStudying && revealed && <div className="revealed-check">✓</div>}
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGINATED WORD LIST
//  v2.1 fix: shuffle preserves Show All
// ─────────────────────────────────────────────
function PaginatedWordList({ words, color, query, studyMode, onCopy, view, isIrregular, activeDiff, onViewChange, onStudyMode, onPlayGame }) {

  const [page, setPage] = useState(1);
  const [shuffledWords, setShuffledWords] = useState(words);
  const [isShuffled, setIsShuffled] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShuffledWords(words);
    setPage(1);
    setIsShuffled(false);
    setShowAll(false);
  }, [words]);

  // Empty state when difficulty filter returns nothing
  if (words.length === 0 && activeDiff && activeDiff !== 'all') {
    return <DifficultyEmpty level={activeDiff} />;
  }

  const searchFiltered = query
    ? shuffledWords.filter(w =>
        w.en?.toLowerCase().includes(query) ||
        w.es?.toLowerCase().includes(query) ||
        w.past?.toLowerCase().includes(query) ||
        w.pp?.toLowerCase().includes(query)
      )
    : shuffledWords;

  const visible = showAll ? searchFiltered : searchFiltered.slice(0, page * PAGE_SIZE);
  const remaining = showAll ? 0 : searchFiltered.length - visible.length;
  // Show level badges when "All" levels shown, so user can see mixed difficulty
  const showLevelBadge = !activeDiff || activeDiff === 'all';

  const handleShuffle = () => {
    setShuffledWords(shuffleArray(shuffledWords));
    setPage(1);
    setIsShuffled(true);
    // ✅ preserves showAll intentionally
  };
  const handleReset = () => {
    setShuffledWords(words);
    setPage(1);
    setIsShuffled(false);
    setShowAll(false);
  };

  const CardComponent = isIrregular ? IrregularWordCard : WordCard;

  return (
    <div className="paginated-list">
      <div className="list-toolbar">
        <span className="list-count">
          {visible.length} / {searchFiltered.length}
          {query ? ' found' : ' words'}
          {showAll && <span style={{ color:'var(--ac)',marginLeft:'.4rem',fontSize:'.58rem' }}>ALL</span>}
        </span>
        <div className="list-actions">
          <button className={`btn-shuffle ${isShuffled ? 'active' : ''}`} onClick={handleShuffle}>
            🔀 Shuffle
          </button>
          <button 
            className="btn-play-game" 
            onClick={() => onPlayGame(searchFiltered)}
            style={{ '--btn-ac': color }}
          >
            🎮 Challenge
          </button>

          {/* ── View toggle inline ── */}
          {onViewChange && (
            <div className="view-toggle-wrap view-toggle-inline">
              <button className={`vtbtn ${view === 'grid' ? 'active' : ''}`} onClick={() => onViewChange('grid')} title="Grid">⊞</button>
              <button className={`vtbtn ${view === 'list' ? 'active' : ''}`} onClick={() => onViewChange('list')} title="List">☰</button>
            </div>
          )}

          {/* ── Study mode inline ── */}
          {onStudyMode && (
            <button
              className={`btn-study-inline ${studyMode !== 'normal' ? 'active' : ''}`}
              onClick={onStudyMode}
              title="Cambiar modo de estudio"
            >
              {studyMode === 'normal' ? '📖' : studyMode === 'study-es' ? '🇬🇧→🇪🇸' : '🇪🇸→🇬🇧'}
            </button>
          )}
        </div>
      </div>

      {isIrregular && view === 'list' && (
        <div className="irr-list-header">
          <span></span><span>Base</span><span>Past Simple</span>
          <span>Past Participle</span><span>Español</span><span></span>
        </div>
      )}

      <div className={view === 'list' ? 'wlist-new' : 'wgrid-new'}>
        {visible.map((w, i) => (
          <CardComponent
            key={`${w.en}-${i}`}
            w={w} color={color} query={query}
            studyMode={studyMode} onCopy={onCopy}
            view={view} index={i}
            showLevelBadge={showLevelBadge}
          />
        ))}
      </div>

      {!showAll && remaining > 0 && (
        <div className="show-more-row">
          <button className="btn-show-more" onClick={() => setPage(p => p + 1)}>
            <span>Show {Math.min(PAGE_SIZE, remaining)} more</span>
            <span className="btn-more-count">{remaining} remaining</span>
          </button>
          <button className="btn-show-all" onClick={() => setShowAll(true)}>
            <span>⚡ Show all</span>
            <span className="btn-more-count">{searchFiltered.length} total</span>
          </button>
        </div>
      )}

      {showAll && searchFiltered.length > PAGE_SIZE && (
        <button className="btn-show-more btn-collapse" onClick={() => { setShowAll(false); setPage(1); }}>
          <span>↑ Collapse</span>
          <span className="btn-more-count">showing all {searchFiltered.length}</span>
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  SUBSECTION CARDS GRID
// ─────────────────────────────────────────────
function SubNavCards({ items, openIdx, onToggle, defaultColor }) {
  return (
    <div className="subsec-cards-grid">
      {items.map((item, si) => {
        const isOpen = openIdx === si;
        const color = item.color || defaultColor;
        const emoji = item.emoji || SUB_EMOJI[item.label] || extractLeadingEmoji(item.label) || '📚';
        const cleanLabel = item.cleanLabel || stripLeadingEmoji(item.label);
        return (
          <button
            key={si}
            className={`subsec-card-btn ${isOpen ? 'active' : ''} ${item.disabled ? 'no-match' : ''}`}
            style={{ '--scard-color': color }}
            onClick={() => onToggle(si)}
            disabled={item.disabled}
          >
            <div className="scard-glow" />
            <span className="scard-emoji">{emoji}</span>
            <div className="scard-label">{cleanLabel}</div>
            {item.count != null && <div className="scard-count" style={{ color }}>{item.count}</div>}
            {isOpen && <div className="scard-active-bar" style={{ background: color }} />}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
//  SUBGROUP SELECTOR (pill row inside subsection)
// ─────────────────────────────────────────────
function SubgroupSelector({ subgroups, activeIdx, color, onSelect, totalReal }) {
  // Sumar los subgrupos cuenta dos veces las palabras que están en dos —«wall»
  // sale doce veces— así que decía 376 donde hay 376 filas pero menos
  // palabras. `totalReal` viene ya deduplicado de arriba; la suma se queda
  // como respaldo por si algún día llega sin ella.
  const allCount = typeof totalReal === 'number'
    ? totalReal
    : subgroups.reduce((acc, sg) => acc + (sg.words?.length || 0), 0);

  const allCard = { emoji:'📚', label:'All', words:{ length: allCount } };
  const cards = [allCard, ...subgroups];

  return (
    <div className="subgroup-card-grid">
      {cards.map((sg, i) => {
        const isAll = i === 0;
        const isActive = isAll ? activeIdx === null : activeIdx === i - 1;
        return (
          <button
            key={i}
            className={`subgroup-card ${isActive ? 'active' : ''}`}
            style={{ '--sg-color': color }}
            onClick={() => onSelect(isAll ? null : (activeIdx === i - 1 ? null : i - 1))}
          >
            <div className="sg-card-glow" />
            <span className="sg-card-emoji">{sg.emoji || '📂'}</span>
            <div className="sg-card-label">{sg.label}</div>
            <div className="sg-card-count" style={{ color }}>{sg.words.length}</div>
            {isActive && <div className="sg-card-bar" style={{ background: color }} />}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
//  ★ SUBSECTION OPEN CONTENT  (v2.2)
//  3-layer filter stack:
//    1. Subgroup pills  →  2. Difficulty filter  →  3. Word list
// ─────────────────────────────────────────────
function SubsectionOpenContent({ sub, color, query, studyMode, onCopy, view, onViewChange, onStudyMode, onPlayGame }) {
  const hasSubgroups = sub.subgroups && sub.subgroups.length > 0;
  const [activeSubgroup, setActiveSubgroup] = useState(null);
  const [activeDiff, setActiveDiff] = useState('all');

  // Reset all filters when subsection changes
  useEffect(() => {
    setActiveSubgroup(null);
    setActiveDiff('all');
  }, [sub.label]);

  // Reset difficulty when subgroup changes
  const handleSubgroupChange = (idx) => {
    setActiveSubgroup(idx);
    setActiveDiff('all');
  };

  // ── TODAS LAS PALABRAS DE LA SUBSECCIÓN, SIN REPETIR ──────────────────
  // `sub.words` son sólo las que NO tienen subgrupo; las que sí lo tienen
  // viven en `sg.words`. Con la tarjeta «All» —la que sale por defecto— se
  // enseñaban únicamente las primeras: en Nouns, 49 de las 376 que la propia
  // tarjeta anunciaba, y en las temáticas grandes, ninguna.
  //
  // Se deduplica porque una palabra puede estar en dos subgrupos a propósito:
  // «wall» está en los doce subgrupos de la casa, porque una pared existe en
  // la cocina y en el baño. Eso en la base es correcto y no se toca; en una
  // lista única se enseña una vez.
  const todasLasDeLaSubseccion = useMemo(() => {
    if (!hasSubgroups) return sub.words || [];
    const vistas = new Set();
    const salida = [];
    const todas = [
      ...(sub.words || []),
      ...sub.subgroups.flatMap(sg => sg.words || []),
    ];
    for (const w of todas) {
      const clave = String(w?.en || '').trim().toLowerCase();
      if (!clave || vistas.has(clave)) continue;
      vistas.add(clave);
      salida.push(w);
    }
    return salida;
  }, [sub, hasSubgroups]);

  // Layer 1: subgroup filter
  const baseWords =
    hasSubgroups && activeSubgroup !== null
      ? sub.subgroups[activeSubgroup].words
      : todasLasDeLaSubseccion;

  // Layer 2: difficulty filter
  const wordsToShow =
    activeDiff === 'all'
      ? baseWords
      : baseWords.filter(w => getWordLevel(w) === activeDiff);

  const activeSubgroupMeta =
    hasSubgroups && activeSubgroup !== null
      ? sub.subgroups[activeSubgroup]
      : null;

  return (
    <div className="subsec-open-content">
      {sub.note && <p className="subsec-open-note">{sub.note}</p>}

      {/* ── Layer 1: Subgroup pills ── */}
      {hasSubgroups && (
        <SubgroupSelector
          subgroups={sub.subgroups}
          activeIdx={activeSubgroup}
          color={color}
          onSelect={handleSubgroupChange}
          totalReal={todasLasDeLaSubseccion.length}
        />
      )}

      {/* Active subgroup label */}
      {activeSubgroupMeta && (
        <div className="subgroup-active-label">
          {activeSubgroupMeta.emoji && `${activeSubgroupMeta.emoji} `}
          {activeSubgroupMeta.label}
          {activeSubgroupMeta.note && (
            <span style={{ fontWeight:400, fontSize:'.62rem', marginLeft:'.5rem', color:'var(--tx2)' }}>
              — {activeSubgroupMeta.note}
            </span>
          )}
        </div>
      )}

      {/* ── Layer 2: Difficulty filter ── */}
      <DifficultyFilter
        words={baseWords}
        activeDiff={activeDiff}
        onSelect={setActiveDiff}
      />

      {/* ── Layer 3: Word list ── */}
      <PaginatedWordList
        words={wordsToShow}
        color={color}
        query={query}
        studyMode={studyMode}
        onCopy={onCopy}
        view={view}
        onViewChange={onViewChange}
        onStudyMode={onStudyMode}
        isIrregular={!!sub.isIrregular}
        activeDiff={activeDiff}
        onPlayGame={onPlayGame}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
//  SECTION CARD
// ─────────────────────────────────────────────
function SectionCard({ secKey, isActive, onClick }) {
  const meta = SECTION_META[secKey];
  if (!meta) return null;
  return (
    <button
      className={`section-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
      style={{ '--card-color': meta.color }}
    >
      <div className="sc-glow" />
      <div className="sc-icon">{meta.icon}</div>
      <div className="sc-label">{meta.label}</div>
      <div className="sc-desc">{meta.desc}</div>
      <div className="sc-count" style={{ color: meta.color }}>{meta.count}</div>
      {isActive && <div className="sc-active-bar" style={{ background: meta.color }} />}
    </button>
  );
}

// ─────────────────────────────────────────────
//  TENSES
// ─────────────────────────────────────────────
function TenseRow({ label, value, cls }) {
  return (
    <div className="tense-row">
      <div className="tense-key">{label}</div>
      <div className="tense-val">
        {cls
          ? <span className={cls} dangerouslySetInnerHTML={{ __html: value }} />
          : <span dangerouslySetInnerHTML={{ __html: value }} />
        }
      </div>
    </div>
  );
}

function TensesSection() {
  const TENSE_GROUPS = [
    {
      key:'present', icon:'⏱', label:'Present', color:'#3b82f6',
      desc:'Simple · Continuous · Perfect · Perf.Cont',
      nums:['01','02','03','04']
    },
    {
      key:'past', icon:'🕰', label:'Past', color:'#22c55e',
      desc:'Simple · Continuous · Perfect · Perf.Cont',
      nums:['05','06','07','08']
    },
    {
      key:'future', icon:'🔮', label:'Future', color:'#f59e0b',
      desc:'Will · Going to · Continuous · Perfect · Perf.Cont',
      nums:['09','10','11','12','13']
    },
    {
      key:'special', icon:'🔧', label:'Special', color:'#a855f7',
      desc:'Passive · Conditionals · Modal + Perfect · Reported Speech',
      nums:[]
    },
  ];

  const [activeGroup, setActiveGroup] = useState(null);
  const [activeTense, setActiveTense] = useState(null);
  const [showAll,     setShowAll]     = useState(false);

  const subGridRef   = useRef(null);
  const tenseCardRef = useRef(null);
  const allGridRef   = useRef(null);

  const handleGroup = (key) => {
    const isClosing = activeGroup === key;
    setActiveGroup(isClosing ? null : key);
    setActiveTense(null);
    setShowAll(false);
    if (!isClosing) {
      setTimeout(() => subGridRef.current?.scrollIntoView({ behavior:'smooth', block:'nearest' }), 120);
    }
  };

  const handleTense = (num) => {
    const isClosing = activeTense === num;
    setActiveTense(isClosing ? null : num);
    if (!isClosing) {
      setTimeout(() => tenseCardRef.current?.scrollIntoView({ behavior:'smooth', block:'nearest' }), 120);
    }
  };

  const handleShowAll = () => {
    const next = !showAll;
    setShowAll(next);
    setActiveGroup(null);
    setActiveTense(null);
    if (next) {
      setTimeout(() => allGridRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }), 120);
    }
  };

  const groupTenses = activeGroup && activeGroup !== 'special'
    ? TENSES.filter(t => TENSE_GROUPS.find(g => g.key === activeGroup)?.nums.includes(t.num))
    : [];

  const activeTenseData = groupTenses.find(t => t.num === activeTense);

  const renderTenseCard = (t) => (
    <div key={t.num} className="tense-card" style={{ '--t-color': t.color }}>
      <div className="tense-title-row">
        <div>
          <div className="tense-num" style={{ color: t.color }}>{t.num} — {t.badge}</div>
          <div className="tense-name" style={{ color: t.color }}>{t.name}</div>
        </div>
        <span className="tense-badge" style={{ background: t.color + '22', color: t.color }}>{t.ph}</span>
      </div>
      <div className="tense-rows">
        <TenseRow label="📌 Use"          value={t.uso}   cls="" />
        <TenseRow label="✅ Positive"      value={t.pos}   cls="pos" />
        <TenseRow label="❌ Negative"      value={t.neg}   cls="neg" />
        <TenseRow label="❓ Question"      value={t.ask}   cls="ask" />
        <TenseRow label="💬 Short Answer"  value={t.short} cls="short" />
        {t.contr && <TenseRow label="🔗 Contraction" value={t.contr} cls="contr" />}
        <TenseRow label="🔥 Example"       value={t.ex}    cls="ex" />
      </div>
    </div>
  );

  return (
    <div className="expanded-inner">

      {/* ── Nivel 1: 4 group cards ── */}
      <div className="tense-group-grid">
        {TENSE_GROUPS.map(g => (
          <button
            key={g.key}
            className={`tense-group-card ${activeGroup === g.key ? 'active' : ''}`}
            style={{ '--tg-color': g.color }}
            onClick={() => handleGroup(g.key)}
          >
            <div className="tg-glow" />
            <span className="tg-icon">{g.icon}</span>
            <div className="tg-label">{g.label}</div>
            <div className="tg-desc">{g.desc}</div>
            {g.nums.length > 0 && (
              <div className="tg-count" style={{ color: g.color }}>{g.nums.length} tenses</div>
            )}
            {activeGroup === g.key && <div className="tg-active-bar" style={{ background: g.color }} />}
          </button>
        ))}
      </div>

      {/* ── Show All button ── */}
      <button
        className={`btn-show-all tense-show-all ${showAll ? 'btn-collapse' : ''}`}
        onClick={handleShowAll}
      >
        <span>{showAll ? '↑ Collapse' : '⚡ Show All'}</span>
        <span className="btn-more-count">13 tenses + Special</span>
      </button>

      {/* ── Nivel 2: sub-cards del grupo ── */}
      {activeGroup && activeGroup !== 'special' && groupTenses.length > 0 && (
        <div ref={subGridRef} className="tense-sub-grid">
          {groupTenses.map(t => (
            <button
              key={t.num}
              className={`tense-sub-card ${activeTense === t.num ? 'active' : ''}`}
              style={{ '--ts-color': t.color }}
              onClick={() => handleTense(t.num)}
            >
              <div className="ts-glow" />
              <div className="ts-num" style={{ color: t.color }}>{t.num}</div>
              <div className="ts-name">{t.name}</div>
              <div className="ts-badge" style={{ background: t.color + '22', color: t.color }}>{t.badge}</div>
              {activeTense === t.num && <div className="ts-active-bar" style={{ background: t.color }} />}
            </button>
          ))}
        </div>
      )}

      {/* ── Nivel 3: tense detail ── */}
      {activeTenseData && (
        <div ref={tenseCardRef} className="tense-card tense-card-single" style={{ '--t-color': activeTenseData.color, animation:'panel-drop .3s ease' }}>
          <div className="tense-title-row">
            <div>
              <div className="tense-num" style={{ color: activeTenseData.color }}>{activeTenseData.num} — {activeTenseData.badge}</div>
              <div className="tense-name" style={{ color: activeTenseData.color }}>{activeTenseData.name}</div>
            </div>
            <span className="tense-badge" style={{ background: activeTenseData.color + '22', color: activeTenseData.color }}>{activeTenseData.ph}</span>
          </div>
          <div className="tense-rows">
            <TenseRow label="📌 Use"          value={activeTenseData.uso}   cls="" />
            <TenseRow label="✅ Positive"      value={activeTenseData.pos}   cls="pos" />
            <TenseRow label="❌ Negative"      value={activeTenseData.neg}   cls="neg" />
            <TenseRow label="🔵 Question"      value={activeTenseData.ask}   cls="ask" />
            <TenseRow label="💬 Short Answer"  value={activeTenseData.short} cls="short" />
            {activeTenseData.contr && <TenseRow label="🔗 Contraction" value={activeTenseData.contr} cls="contr" />}
            <TenseRow label="🔥 Example"       value={activeTenseData.ex}    cls="ex" />
          </div>
        </div>
      )}

      {/* ── Special Structures ── */}
      {activeGroup === 'special' && (
        <div ref={subGridRef} style={{ animation:'panel-drop .3s ease' }}>
          <div className="sub-lbl" style={{ color:'#a855f7' }}>
            🔧 Special Structures — Passive · Conditionals · Modal+Perfect · Reported Speech
          </div>
          <div className="special-grid">
            {SPECIALS.map((s, si) => (
              <div key={si} className="spec-card" style={{ '--spec-color': s.color }}>
                <div className="spec-title" style={{ color: s.color }}>{s.title}</div>
                <div className="spec-rows">
                  {s.rows.map((r, ri) => (
                    <div key={ri} className="spec-row">
                      <b style={{ color: s.color }}>{r.k}:</b>{' '}
                      <span dangerouslySetInnerHTML={{ __html: r.v }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Show All: todos los tenses expandidos + Special ── */}
      {showAll && (
        <div ref={allGridRef} style={{ animation:'panel-drop .3s ease' }}>
          <div className="tenses-grid">
            {TENSES.map(t => renderTenseCard(t))}
          </div>
          <div className="sub-lbl" style={{ color:'#a855f7', marginTop:'1.5rem' }}>
            🔧 Special Structures — Passive · Conditionals · Modal+Perfect · Reported Speech
          </div>
          <div className="special-grid">
            {SPECIALS.map((s, si) => (
              <div key={si} className="spec-card" style={{ '--spec-color': s.color }}>
                <div className="spec-title" style={{ color: s.color }}>{s.title}</div>
                <div className="spec-rows">
                  {s.rows.map((r, ri) => (
                    <div key={ri} className="spec-row">
                      <b style={{ color: s.color }}>{r.k}:</b>{' '}
                      <span dangerouslySetInnerHTML={{ __html: r.v }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────
//  SUBSECTION ACCORDION
// ─────────────────────────────────────────────
function SubsectionAccordion({ subs, color, query, studyMode, onCopy, view, onViewChange, onStudyMode, onPlayGame }) {
  const firstVisible = query
    ? subs.findIndex(sub =>
        sub.words.some(w => w.en?.toLowerCase().includes(query) || w.es?.toLowerCase().includes(query))
      )
    : 0;
  const [openIdx, setOpenIdx] = useState(firstVisible >= 0 ? firstVisible : 0);
  const contentRef = useRef(null);

  useEffect(() => {
    if (query) {
      const idx = subs.findIndex(sub =>
        sub.words.some(w => w.en?.toLowerCase().includes(query) || w.es?.toLowerCase().includes(query))
      );
      if (idx >= 0) setOpenIdx(idx);
    }
  }, [query, subs]);

  useEffect(() => {
    if (openIdx !== null) {
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 120);
    }
  }, [openIdx]);

  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);
  const navItems = subs.map((sub) => {
    const matchCount = query
      ? sub.words.filter(w => w.en?.toLowerCase().includes(query) || w.es?.toLowerCase().includes(query)).length
      : sub.words.length;
    return {
      label: sub.label,
      count: query ? `${matchCount}/${sub.words.length}` : `${sub.words.length} words`,
      disabled: query ? matchCount === 0 : false,
      color,
    };
  });

  return (
    <div className="subsec-accordion">
      <SubNavCards items={navItems} openIdx={openIdx} onToggle={toggle} defaultColor={color} />
      {openIdx !== null && subs[openIdx] && (
        <div ref={contentRef}>
          <SubsectionOpenContent
            key={openIdx}
            sub={subs[openIdx]}
            color={color}
            query={query}
            studyMode={studyMode}
            onCopy={onCopy}
            view={view}
            onViewChange={onViewChange}
            onStudyMode={onStudyMode}
            onPlayGame={onPlayGame}
          />
        </div>
      )}
    </div>
  );
}

// 🚀 ACTUALIZADO: Ahora recibe dynamicSections
function StandardSectionExpanded({ secKey, query, studyMode, onCopy, view, onViewChange, onStudyMode, onPlayGame, dynamicSections }) {
  const meta = SECTION_META[secKey];
  const secData = dynamicSections[secKey]; // 👈 Usa la versión rellenada
  if (!secData || !meta) return null;
  return (
    <div className="expanded-inner">
      <SubsectionAccordion
        subs={secData.subs} color={meta.color}
        query={query} studyMode={studyMode}
        onCopy={onCopy} view={view}
        onViewChange={onViewChange}
        onStudyMode={onStudyMode}
        onPlayGame={onPlayGame} 
      />
    </div>
  );
}

// ─────────────────────────────────────────────
//  COLLOCATIONS
// ─────────────────────────────────────────────

function CollItem({ w, verb, dotColor, textColor, query, onCopy, studyMode }) {
  const [revealed, setRevealed] = useState(false);
  const isStudying = studyMode !== 'normal';
  const hideES = studyMode === 'study-es';
  const hideEN = studyMode === 'study-en';

  const handleClick = () => { if (isStudying) setRevealed(r => !r); else onCopy(w.en); };
  useEffect(() => { setRevealed(false); }, [studyMode]);

  const firstSpaceIdx = w.en.indexOf(' ');
  let verbPart = firstSpaceIdx === -1 ? w.en : w.en.slice(0, firstSpaceIdx);
  const complementPart = firstSpaceIdx === -1 ? '' : w.en.slice(firstSpaceIdx);
  verbPart = verbPart.charAt(0).toUpperCase() + verbPart.slice(1);

  // 🚀 MAGIA ANTI-FALLOS: Desenfocamos forzando a React, sin depender del CSS externo
  const blurStyle = {
    filter: 'blur(4.5px)',
    opacity: 0.35,
    pointerEvents: 'none' // Evita que se seleccione el texto por accidente
  };

  return (
    <div 
      className={`coll-item ${isStudying ? 'studying' : ''} ${revealed ? 'revealed' : ''}`} 
      onClick={handleClick}
      style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem', padding: '0.6rem 0.8rem', position: 'relative', overflow: 'hidden' }}
    >
      {/* 🇬🇧 TEXTO EN INGLÉS */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '85%', transition: 'all 0.3s ease', ...(hideEN && !revealed ? blurStyle : {}) }}>
        <span className="coll-ok-dot" style={{ background: dotColor, marginTop: '2px', flexShrink: 0 }} />
        <span style={{ fontSize: '0.9rem' }}>
          <strong style={{ fontWeight: 700 }}><Highlight text={verbPart} query={query} /></strong>
          <span style={{ fontWeight: 400 }}><Highlight text={complementPart} query={query} /></span>
        </span>
      </div>
      
      {/* 🇪🇸 TEXTO EN ESPAÑOL */}
      <div style={{ fontSize: '0.75rem', color: textColor, paddingLeft: '1rem', width: '85%', opacity: 0.9, transition: 'all 0.3s ease', ...(hideES && !revealed ? blurStyle : {}) }}>
        <Highlight text={w.es} query={query} />
      </div>

      {/* 👁 ICONOS DE MODO ESTUDIO (Alineados perfectos a la derecha) */}
      {isStudying && !revealed && (
        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.2rem', opacity: 0.5 }}>👁</span>
      )}
      {isStudying && revealed && (
        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.1rem', color: dotColor }}>✓</span>
      )}
    </div>
  );
}


function CollVerbCol({ verb, words, dotColor, bgMix, textColor, query, onCopy, studyMode }) {
  const filtered = words.filter(w => !query || w.en.toLowerCase().includes(query) || w.es.toLowerCase().includes(query));
  return (
    <div className="coll-col">
      <div className="coll-verb-badge" style={{ background: bgMix, color: textColor }}>✅ {verb}</div>
      <div className="coll-items">
        {filtered.map((w, i) => (
          <CollItem key={i} w={w} verb={verb} dotColor={dotColor} textColor={textColor} query={query} onCopy={onCopy} studyMode={studyMode} />
        ))}
      </div>
    </div>
  );
}

function CollocationsExpanded({ query, onCopy, studyMode, view, onViewChange, onStudyMode, color, onPlayGame }) {
  const c = color || '#f59e0b';
  const COLL_SUBS = [
    { label:'MAKE vs DO',   emoji:'⚔️',  cleanLabel:'MAKE vs DO',      note:'El error #1 — ¡solo memoria!' },
    { label:'HAVE vs TAKE', emoji:'🤲',  cleanLabel:'HAVE vs TAKE',    note:'UK/US register differences' },
    { label:'GET vs KEEP',  emoji:'🔄',  cleanLabel:'GET vs KEEP',     note:'Dinamismo vs Retención' },
    { label:COLL_DATA.subs[0]?.label||'Strong Verbs', emoji:'💪', cleanLabel:'Strong Verb Collos', note:COLL_DATA.subs[0]?.note },
    { label:COLL_DATA.subs[1]?.label||'Adj + Noun',   emoji:'⚡', cleanLabel:'Adj + Noun',         note:COLL_DATA.subs[1]?.note },
    { label:COLL_DATA.subs[2]?.label||'Idioms',       emoji:'🏆', cleanLabel:'Biz Idioms',          note:COLL_DATA.subs[2]?.note },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);
  const navItems = COLL_SUBS.map(s => ({ ...s, color: c }));

  // 🚀 Magia de Shuffle para las columnas dobles
  const [isShuffled, setIsShuffled] = useState(false);
  const [makeWords, setMakeWords] = useState(COLL_DATA.makeWords);
  const [doWords, setDoWords] = useState(COLL_DATA.doWords);
  const [haveWords, setHaveWords] = useState(COLL_DATA.haveWords);
  const [takeWords, setTakeWords] = useState(COLL_DATA.takeWords);
  const [getWords, setGetWords] = useState(COLL_DATA.getWords);
  const [keepWords, setKeepWords] = useState(COLL_DATA.keepWords);

  useEffect(() => {
    setIsShuffled(false);
    setMakeWords(COLL_DATA.makeWords); setDoWords(COLL_DATA.doWords);
    setHaveWords(COLL_DATA.haveWords); setTakeWords(COLL_DATA.takeWords);
    setGetWords(COLL_DATA.getWords);   setKeepWords(COLL_DATA.keepWords);
  }, [openIdx]);

  const handleShuffleVs = () => {
    setMakeWords(shuffleArray(COLL_DATA.makeWords)); setDoWords(shuffleArray(COLL_DATA.doWords));
    setHaveWords(shuffleArray(COLL_DATA.haveWords)); setTakeWords(shuffleArray(COLL_DATA.takeWords));
    setGetWords(shuffleArray(COLL_DATA.getWords));   setKeepWords(shuffleArray(COLL_DATA.keepWords));
    setIsShuffled(true);
  };

  const renderVsToolbar = () => (
    <div className="list-toolbar" style={{ marginBottom: '1rem' }}>
      <span className="list-count"></span>
      <div className="list-actions">
        <button className={`btn-shuffle ${isShuffled ? 'active' : ''}`} onClick={handleShuffleVs}>🔀 Shuffle</button>
        {onStudyMode && (
          <button className={`btn-study-inline ${studyMode !== 'normal' ? 'active' : ''}`} onClick={onStudyMode} title="Cambiar modo de estudio">
            {studyMode === 'normal' ? '📖' : studyMode === 'study-es' ? '🇬🇧→🇪🇸' : '🇪🇸→🇬🇧'}
          </button>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    if (openIdx === null) return null;
    if (openIdx === 0) return (
      <>
        {renderVsToolbar()}
        <div className="coll-pair-grid">
          <CollVerbCol verb="MAKE" words={makeWords} dotColor="var(--ok)" bgMix="color-mix(in srgb,var(--ok) 20%,var(--bg))" textColor="var(--ok)" query={query} onCopy={onCopy} studyMode={studyMode} />
          <CollVerbCol verb="DO" words={doWords} dotColor="#60a5fa" bgMix="color-mix(in srgb,#60a5fa 20%,var(--bg))" textColor="#60a5fa" query={query} onCopy={onCopy} studyMode={studyMode} />
        </div>
      </>
    );
    if (openIdx === 1) return (
      <>
        {renderVsToolbar()}
        <div className="coll-pair-grid">
          <CollVerbCol verb="HAVE" words={haveWords} dotColor="#f472b6" bgMix="color-mix(in srgb,#f472b6 20%,var(--bg))" textColor="#f472b6" query={query} onCopy={onCopy} studyMode={studyMode} />
          <CollVerbCol verb="TAKE" words={takeWords} dotColor="#38bdf8" bgMix="color-mix(in srgb,#38bdf8 20%,var(--bg))" textColor="#38bdf8" query={query} onCopy={onCopy} studyMode={studyMode} />
        </div>
      </>
    );
    if (openIdx === 2) return (
      <>
        {renderVsToolbar()}
        <div className="coll-pair-grid">
          <CollVerbCol verb="GET" words={getWords} dotColor="#10b981" bgMix="color-mix(in srgb,#10b981 20%,var(--bg))" textColor="#10b981" query={query} onCopy={onCopy} studyMode={studyMode} />
          <CollVerbCol verb="KEEP" words={keepWords} dotColor="#8b5cf6" bgMix="color-mix(in srgb,#8b5cf6 20%,var(--bg))" textColor="#8b5cf6" query={query} onCopy={onCopy} studyMode={studyMode} />
        </div>
      </>
    );
    
    // 🚀 INYECCIÓN MAESTRA: Usamos el PaginatedWordList para el resto de categorías
    const sub = COLL_DATA.subs[openIdx - 3];
    if (!sub) return null;
    return (
      <SubsectionOpenContent
        sub={sub} color={c} query={query}
        studyMode={studyMode} onCopy={onCopy} view={view}
        onViewChange={onViewChange} onStudyMode={onStudyMode}
        onPlayGame={onPlayGame}
      />
    );
  };

  return (
    <div className="expanded-inner">
      <div className="subsec-accordion">
        <SubNavCards items={navItems} openIdx={openIdx} onToggle={toggle} defaultColor={c} />
        {openIdx !== null && (
          <div className="subsec-open-content" key={openIdx}>
            {COLL_SUBS[openIdx]?.note && <p className="subsec-open-note">{COLL_SUBS[openIdx].note}</p>}
            {renderContent()}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  SYNTAX
// ─────────────────────────────────────────────
function SyntaxCard({ card }) {
  return (
    <div className="syn-card">
      <div className="syn-title-row" style={{ borderColor: card.color + '88' }}>
        <div>
          <div className="syn-label" style={{ color: card.color }}>Advanced Syntax</div>
          <div className="syn-name" style={{ color: card.color }}>{card.name}</div>
        </div>
        <span className="syn-level" style={{ background: card.color + '22', color: card.color }}>{card.level}</span>
      </div>
      <div className="syn-struct" style={{ background: card.color + '15', color: card.color }}>{card.struct}</div>
      <div className="syn-rule">{card.rule}</div>
      <div className="syn-ex-lbl">Transformaciones:</div>
      {card.items.map((item, ii) => (
        <div key={ii} className="syn-transform">
          <div className="syn-plain" style={{ background:'color-mix(in srgb,var(--er) 12%,var(--bg))' }}>{item.plain}</div>
          <div className="syn-arrow-badge">→</div>
          <div className="syn-advanced" style={{ background:'color-mix(in srgb,var(--ok) 12%,var(--bg))' }}>
            <span dangerouslySetInnerHTML={{ __html: item.adv }} />
            {item.note && <span style={{ fontSize:'.6rem',color:'var(--tx2)' }}> {item.note}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
function SyntaxExpanded() {
  return (
    <div className="expanded-inner">
      <div className="syntax-grid">
        {SYNTAX_DATA.map((card, ci) => <SyntaxCard key={ci} card={card} />)}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PRONUNCIATION
// ─────────────────────────────────────────────
function PronunciationExpanded() {
  const c = '#fb7185';
  const PRON_SUBS = [
    { label:'🔗 Connected Speech', emoji:'🔗', cleanLabel:'Connected Speech' },
    { label:'🗣️ Vowel Sounds',    emoji:'🗣️', cleanLabel:'Vowel Sounds' },
    { label:'👂 Minimal Pairs',   emoji:'👂', cleanLabel:'Minimal Pairs' },
    { label:'🤫 Silent Letters',  emoji:'🤫', cleanLabel:'Silent Letters' },
    { label:'🎯 Word Stress',     emoji:'🎯', cleanLabel:'Word Stress' },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);
  const navItems = PRON_SUBS.map(s => ({ ...s, color: c }));

  const renderContent = () => {
    if (openIdx === 0) return (
      <div className="speech-rule-grid">
        {PRON_DATA.connectedSpeech.map((r, i) => (
          <div key={i} className="speech-rule-card">
            <div className="speech-type" style={{ color: c }}>{r.type}</div>
            <div className="speech-name">{r.name}</div>
            <div className="speech-def">{r.def}</div>
            <div className="speech-ex-row">
              <span className="speech-from">{r.from}</span>
              <span className="speech-arrow">→</span>
              <span className="speech-to">{r.to}</span>
            </div>
            <div className="speech-hint">{r.hint}</div>
          </div>
        ))}
      </div>
    );
    if (openIdx === 1) return (
      <div className="vowel-grid">
        {PRON_DATA.vowelSounds.map((v, i) => (
          <div key={i} className="vowel-card">
            <div className="vowel-name" style={{ color: v.color }}>{v.name}</div>
            <div className="vowel-ipa" style={{ color: v.color }}>{v.ipa}</div>
            <div className="vowel-words">
              {v.words.map((w, wi) => <span key={wi} className="vowel-word" style={{ color: v.color }}>{w}</span>)}
            </div>
            <div className="vowel-tip">{v.tip}</div>
          </div>
        ))}
      </div>
    );
    if (openIdx === 2) return (
      <div className="mpair-grid">
        {PRON_DATA.minimalPairs.map((p, i) => (
          <div key={i} className="mpair-card">
            <div className="mpair-side"><div className="mpair-word">{p.a}</div><div className="mpair-ipa" style={{ color: c }}>[{p.aipa}]</div></div>
            <div className="mpair-div">vs</div>
            <div className="mpair-side"><div className="mpair-word">{p.b}</div><div className="mpair-ipa" style={{ color: c }}>[{p.bipa}]</div></div>
          </div>
        ))}
      </div>
    );
    if (openIdx === 3) return (
      <div className="silent-grid">
        {PRON_DATA.silentLetters.map((s, i) => {
          const silentChar = s.silent.split('+')[0];
          const regex = new RegExp(silentChar, 'i');
          const parts = s.word.split(regex);
          const match = s.word.match(regex);
          return (
            <div key={i} className="silent-card">
              <div className="silent-word">
                {parts[0]}{match && <span className="silent-letter">{match[0]}</span>}{parts[1]}
              </div>
              <div className="silent-ipa">{s.ipa}</div>
              <div className="silent-tip">{s.more || s.note || ''}</div>
            </div>
          );
        })}
      </div>
    );
    if (openIdx === 4) return (
      <div className="stress-grid">
        {PRON_DATA.wordStress.map((s, i) => (
          <div key={i} className="stress-card">
            <div className="stress-rule" style={{ color: c }}>{s.rule}</div>
            <div className="stress-examples">
              {s.examples.map((e, ei) => (
                <div key={ei} className="stress-ex">
                  <span className="stress-word">{e.w}</span>
                  {e.t && <span className="stress-type">({e.t})</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
    return null;
  };

  return (
    <div className="expanded-inner">
      <div className="subsec-accordion">
        <SubNavCards items={navItems} openIdx={openIdx} onToggle={toggle} defaultColor={c} />
        {openIdx !== null && (
          <div className="subsec-open-content" key={openIdx}>{renderContent()}</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  SPEAKING
// ─────────────────────────────────────────────
function SpeakingExpanded({ query, studyMode }) {
  const c = '#34d399';
  const [openIdx, setOpenIdx] = useState(0);
  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);
  const navItems = SPEAKING_DATA.map((cat) => {
    const matchCount = query
      ? cat.items.filter(i => i.phrase?.toLowerCase().includes(query) || i.es?.toLowerCase().includes(query)).length
      : cat.items.length;
    return {
      label: cat.cat, emoji: extractLeadingEmoji(cat.cat) || '💬',
      cleanLabel: stripLeadingEmoji(cat.cat),
      count: query ? `${matchCount}/${cat.items.length}` : `${cat.items.length} phrases`,
      disabled: query ? matchCount === 0 : false, color: c,
    };
  });

  return (
    <div className="expanded-inner">
      <div className="subsec-accordion">
        <SubNavCards items={navItems} openIdx={openIdx} onToggle={toggle} defaultColor={c} />
        {openIdx !== null && SPEAKING_DATA[openIdx] && (() => {
          const cat = SPEAKING_DATA[openIdx];
          const filtered = query
            ? cat.items.filter(i => i.phrase?.toLowerCase().includes(query) || i.es?.toLowerCase().includes(query))
            : cat.items;
          return (
            <div className="subsec-open-content" key={openIdx}>
              <div className="spk-grid">
                {filtered.map((item, ii) => (
                  <div key={ii} className="spk-card">
                    <div className="spk-situation" style={{ color: c }}>Phrase {String(ii + 1).padStart(2, '0')}</div>
                    <div className="spk-phrase">"{item.phrase}"</div>
                    <div className="spk-es">{item.es}</div>
                    <span className="spk-level" style={{ background:`color-mix(in srgb,${c} 20%,var(--bg))`,color:c }}>{item.level}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PRAGMATICS
// ─────────────────────────────────────────────
function PragmaticsExpanded({ query, onCopy }) {
  const c = '#60a5fa';
  const PRAG_SUBS = [
    { label:'🇬🇧 British Understatement', emoji:'🇬🇧', cleanLabel:'British Understatement', color:c },
    { label:'📊 Register Ladder',          emoji:'📊', cleanLabel:'Register Ladder',         color:c },
    { label:'🤝 Face-Saving Language',     emoji:'🫱', cleanLabel:'Face-Saving Language',    color:c },
    { label:'☕ Small Talk',               emoji:'☕', cleanLabel:'Small Talk',               color:c },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);

  const renderContent = () => {
    if (openIdx === 0) return (
      <div className="brit-grid">
        {PRAGM_DATA.britishUnderstatement
          .filter(b => !query || b.says?.toLowerCase().includes(query) || b.means?.toLowerCase().includes(query))
          .map((b, i) => (
            <div key={i} className="brit-card" onClick={() => onCopy(b.says)}>
              <div className="brit-says">"{b.says}"</div>
              <div className="brit-means-lbl">REALLY MEANS:</div>
              <div className="brit-means">{b.means}</div>
              <div className="brit-tip">{b.tip}</div>
            </div>
          ))}
      </div>
    );
    if (openIdx === 1) return (
      <div className="prag-grid">
        {PRAGM_DATA.registerLadder.map((scenario, si) => (
          <div key={si} className="prag-card">
            <div className="prag-head">
              <span className="prag-icon">{scenario.icon}</span>
              <span style={{ color: c }}>{scenario.msg}</span>
            </div>
            {scenario.levels.map((l, li) => (
              <div key={li} className="prag-level-row" onClick={() => onCopy(l.text)}>
                <div className="prag-tag" style={{ color: l.color }}>{l.tag}</div>
                <div className="prag-text">{l.text}</div>
              </div>
            ))}
            <div className="prag-note">💡 {scenario.note}</div>
          </div>
        ))}
      </div>
    );
    if (openIdx === 2) return (
      <div className="spk-grid">
        {PRAGM_DATA.faceSaving
          .filter(f => !query || f.phrase?.toLowerCase().includes(query))
          .map((f, i) => (
            <div key={i} className="spk-card" onClick={() => onCopy(f.phrase)}>
              <div className="spk-situation" style={{ color: c }}>Face-Saving</div>
              <div className="spk-phrase">"{f.phrase}"</div>
              <div className="spk-es">{f.es}</div>
              <span className="spk-level" style={{ background:`color-mix(in srgb,${c} 20%,var(--bg))`,color:c }}>{f.note}</span>
            </div>
          ))}
      </div>
    );
    if (openIdx === 3) return (
      <div className="prag-grid">
        {PRAGM_DATA.smallTalk.map((t, i) => {
          const clr = t.safe ? '#34d399' : '#fb7185';
          return (
            <div key={i} className="prag-card">
              <div className="prag-head">
                <span className="prag-icon">{t.safe ? '✅' : '❌'}</span>
                <span style={{ color:clr, fontWeight:700 }}>{t.topic}</span>
              </div>
              <div className="prag-level-row" style={{ boxShadow:'none',padding:'.3rem 0' }}>
                <div className="prag-text" style={{ fontStyle:'italic',color:'var(--tx2)' }}>"{t.example}"</div>
              </div>
              <div className="prag-note">{t.note}</div>
            </div>
          );
        })}
      </div>
    );
    return null;
  };

  return (
    <div className="expanded-inner">
      <div className="subsec-accordion">
        <SubNavCards items={PRAG_SUBS} openIdx={openIdx} onToggle={toggle} defaultColor={c} />
        {openIdx !== null && (
          <div className="subsec-open-content" key={openIdx}>{renderContent()}</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  THEMATIC (ACTUALIZADO: recibe dynamicThematic)
// ─────────────────────────────────────────────
function ThematicExpanded({ query, studyMode, onCopy, view, onViewChange, onStudyMode, dynamicThematic, onPlayGame }) {
  const [openIdx, setOpenIdx] = useState(0);
  const contentRef = useRef(null);

  // 🚀 Usa los datos de Neon si existen, si no usa los estáticos
  const thematicSource = dynamicThematic?.length > 0 ? dynamicThematic : THEMATIC_DATA;

  // 🚀 NUEVO: Helper para extraer todas las palabras (las planas + las de los subgrupos)
  // para que el contador general de la tarjeta sume todo correctamente.
  const getAllWords = (sub) => {
    let all = [...(sub.words || [])];
    if (sub.subgroups) {
      sub.subgroups.forEach(sg => {
        all = all.concat(sg.words || []);
      });
    }
    return all;
  };

  useEffect(() => {
    if (query) {
      const idx = thematicSource.findIndex(sub => {
        const allW = getAllWords(sub);
        return allW.some(w => w.en?.toLowerCase().includes(query) || w.es?.toLowerCase().includes(query));
      });
      if (idx >= 0) setOpenIdx(idx);
    }
  }, [query, thematicSource]);

  useEffect(() => {
    if (openIdx !== null) {
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 120);
    }
  }, [openIdx]);

  const toggle = (idx) => setOpenIdx(prev => prev === idx ? null : idx);
  
  const navItems = thematicSource.map((sub) => {
    const allW = getAllWords(sub); // Usamos el helper para contar todo
    const matchCount = query
      ? allW.filter(w => w.en?.toLowerCase().includes(query) || w.es?.toLowerCase().includes(query)).length
      : allW.length;
    return {
      label: sub.label, emoji: extractLeadingEmoji(sub.label) || '📚',
      cleanLabel: stripLeadingEmoji(sub.label),
      count: query ? `${matchCount}/${allW.length}` : `${allW.length} words`,
      disabled: query ? matchCount === 0 : false, color: sub.color,
    };
  });

  return (
    <div className="expanded-inner">
      <div className="subsec-accordion">
        <SubNavCards items={navItems} openIdx={openIdx} onToggle={toggle} defaultColor="#fbbf24" />
        {openIdx !== null && thematicSource[openIdx] && (() => {
          const sub = thematicSource[openIdx];
          return (
            <div ref={contentRef} key={openIdx}>
              {/* 🚀 LA MAGIA: Reemplazamos PaginatedWordList por SubsectionOpenContent
                  para que detecte automáticamente los subgroups y pinte los botones! */}
              <SubsectionOpenContent
                sub={sub} 
                color={sub.color} 
                query={query}
                studyMode={studyMode} 
                onCopy={onCopy} 
                view={view}
                onViewChange={onViewChange}
                onStudyMode={onStudyMode}
                onPlayGame={onPlayGame}
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  EXPANDED SECTION PANEL (ACTUALIZADO: recibe dynamicSections, dynamicThematic)
// ─────────────────────────────────────────────
function ExpandedSectionPanel({ secKey, query, studyMode, onCopy, view: _parentView, onClose, onStudyCycle, onPlayGame, dynamicSections, dynamicThematic }) {
  const meta = SECTION_META[secKey];
  const [localView, setLocalView] = useState('grid');

  if (!meta) return null;

  const renderContent = () => {
    switch (secKey) {
      case 'tenses':        return <TensesSection />;
      case 'collocations':  return <CollocationsExpanded query={query} onCopy={onCopy} studyMode={studyMode} view={localView} onViewChange={setLocalView} onStudyMode={onStudyCycle} color={meta.color} onPlayGame={onPlayGame} />;
      case 'syntax':        return <SyntaxExpanded />;
      case 'pronunciation': return <PronunciationExpanded />;
      case 'speaking':      return <SpeakingExpanded query={query} studyMode={studyMode} />;
      case 'pragmatics':    return <PragmaticsExpanded query={query} onCopy={onCopy} />;
      case 'thematic':      
        return <ThematicExpanded 
          query={query} 
          studyMode={studyMode} 
          onCopy={onCopy} 
          view={localView} 
          onViewChange={setLocalView} 
          onStudyMode={onStudyCycle} 
          onPlayGame={onPlayGame} 
          dynamicThematic={dynamicThematic}  // 👈 PASAMOS LOS DATOS DINÁMICOS
        />;
      default:
        if (dynamicSections[secKey]) {  // 👈 USAMOS dynamicSections
          return <StandardSectionExpanded 
            secKey={secKey} 
            query={query} 
            studyMode={studyMode} 
            onCopy={onCopy} 
            view={localView} 
            onViewChange={setLocalView} 
            onStudyMode={onStudyCycle} 
            onPlayGame={onPlayGame} 
            dynamicSections={dynamicSections}  // 👈 PASAMOS LOS DATOS DINÁMICOS
          />;
        }
        return null;
    }
  };

  return (
    <div className="expanded-panel" style={{ '--panel-color': meta.color }}>
      <div className="expanded-header">
        <div className="expanded-header-left">
          <span className="expanded-icon">{meta.icon}</span>
          <div>
            <div className="expanded-title" style={{ color: meta.color }}>{meta.label}</div>
            <div className="expanded-desc">{meta.desc}</div>
          </div>
        </div>
        <div className="expanded-header-right">
          <span className="expanded-count" style={{ color: meta.color }}>{meta.count}</span>
          
          <button className="btn-close-panel" onClick={onClose} title="Close section">✕</button>
        </div>
      </div>
      <div className="expanded-content">{renderContent()}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  STUDY MODE
// ─────────────────────────────────────────────
const STUDY_MODES = [
  { key:'normal',   label:'📖 Normal',  hint:'Show everything' },
  { key:'study-es', label:'🇬🇧 → 🇪🇸',  hint:'See English, reveal Spanish' },
  { key:'study-en', label:'🇪🇸 → 🇬🇧',  hint:'See Spanish, reveal English' },
];

// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────
export default function GlossaryPanel() {
  const [searchQuery,   setSearchQuery]   = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [currentView,   setCurrentView]   = useState('grid');
  const [studyMode,     setStudyMode]     = useState('normal');
  const [isDark,        setIsDark]        = useState(false);
  const [showToast,     setShowToast]     = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [gameWords, setGameWords] = useState(null); // null = cerrado, Array = abierto
  const expandedRef = useRef(null);

  // 🚀 1. EXTRAEMOS LA SANGRE DE NEON
  const { words: neonWords, loading, error } = useGlossaryData();

  // 🧠 2. EL MEZCLADOR NEURONAL (Esqueleto Estático + Datos de Neon)
  const { dynamicSections, dynamicThematic } = useMemo(() => {
    // Clonamos profundamente los esqueletos para no mutar los originales
    const mergedSections = JSON.parse(JSON.stringify(SECTIONS));
    const mergedThematic = JSON.parse(JSON.stringify(THEMATIC_DATA));

    if (!neonWords || neonWords.length === 0) {
      return { dynamicSections: mergedSections, dynamicThematic: mergedThematic };
    }

    // Inyectamos cada palabra en su lugar anatómico correcto
    neonWords.forEach(w => {
      // ── THEMATIC DATA ──
      if (w.section === 'thematic') {
        const theme = mergedThematic.find(t => t.label === w.subsection);
        if (theme) {
          if (w.subgroup && theme.subgroups) {
            const sg = theme.subgroups.find(g => g.label === w.subgroup);
            if (sg) {
              sg.words = sg.words || [];
              sg.words.push(w);
            }
          } else {
            theme.words = theme.words || [];
            theme.words.push(w);
          }
        }
      } 
      // ── GENERAL SECTIONS (Nouns, Verbs, etc.) ──
      else if (mergedSections[w.section]) {
        const sec = mergedSections[w.section];
        if (sec.subs) {
          const sub = sec.subs.find(s => s.label === w.subsection);
          if (sub) {
            if (w.subgroup && sub.subgroups) {
              const sg = sub.subgroups.find(g => g.label === w.subgroup);
              if (sg) {
                sg.words = sg.words || [];
                sg.words.push(w);
              }
            } else {
              sub.words = sub.words || [];
              sub.words.push(w);
            }
          }
        }
      }
    });

    return { dynamicSections: mergedSections, dynamicThematic: mergedThematic };
  }, [neonWords]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (activeSection && expandedRef.current) {
      setTimeout(() => {
        expandedRef.current?.scrollIntoView({ behavior:'smooth', block:'start' });
      }, 100);
    }
  }, [activeSection]);

  // 🔊 NUEVO MOTOR DE VOZ NATIVO (Text-to-Speech)
  const speakWord = useCallback((text) => {
    // 1. Cancelamos cualquier voz anterior para que no se encimen
    window.speechSynthesis.cancel(); 
    
    // 2. Creamos la locución
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-GB'; // 🇬🇧 Forzamos acento británico (Great Britain)
    utterance.rate = 0.9;     // 🐢 Un poco más lento para mejor claridad de estudio
    utterance.pitch = 1.0;    // Tono normal
    
    // 3. ¡Habla!
    window.speechSynthesis.speak(utterance);

    // 4. Mostramos el toast de confirmación visual
    setShowToast(true);
    setTimeout(() => setShowToast(false), 1600);
  }, []);

  const handleSectionClick = useCallback((secKey) => {
    setActiveSection(prev => prev === secKey ? null : secKey);
  }, []);

  const cycleStudyMode = () => {
    setStudyMode(prev => {
      const idx = STUDY_MODES.findIndex(m => m.key === prev);
      return STUDY_MODES[(idx + 1) % STUDY_MODES.length].key;
    });
  };

  const currentStudyMeta = STUDY_MODES.find(m => m.key === studyMode);
  const allSectionKeys = Object.keys(SECTION_META);

  return (
    <div className="glossary-root" data-theme={isDark ? 'dark' : ''}>

      {/* ── HERO ── */}
      <div className="glos-hero">
        <div className="glos-hero-inner">
          <div className="glos-hero-tag">Master Glossary — Pareto 80/20 · God Tier Edition</div>
          <h1>ENGLISH <em>A1→C2</em></h1>
          <div className="glos-hero-stats">
            {['16 SECCIONES','13 VERB TENSES','2,400+ ENTRIES','PHONETICS','SYNTAX C1/C2','STUDY MODE'].map(s => (
              <span key={s} className="glos-stat">{s}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTROLS BAR ── */}
      <div className="controls-bar">
        <input
            maxLength={80}
          className="search"
          placeholder="🔍  Search anything... (e.g. freedom, get up, deploy, inversion)"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value.toLowerCase())}
        />
        <button
          className={`btn-study-mode ${studyMode !== 'normal' ? 'active' : ''}`}
          onClick={cycleStudyMode}
          title={currentStudyMeta.hint}
        >
          <span className="study-mode-label">{currentStudyMeta.label}</span>
          {studyMode !== 'normal' && (
            <span className="study-mode-hint">{currentStudyMeta.hint}</span>
          )}
        </button>
        
      </div>

      {/* ── SECTION CARDS GRID ── */}
      <div className="glossary-content">
        {/* ── PANTALLA DE CARGA NEÓN PREMIUM ── */}
        {loading && (
          <div className="neural-loader-container">
            <div className="neon-spinner" />
            <div className="neon-loader-text">
              Sincronizando Base Neuronal...
            </div>
          </div>
        )}
        
        {error && (
          <div style={{ textAlign: 'center', width: '100%', padding: '2rem', color: 'var(--er)' }}>
            ❌ Falla en la matrix: {error}
          </div>
        )}

        {!loading && !error && GROUPS.map(group => {
          const groupSections = allSectionKeys.filter(k => SECTION_META[k].group === group);
          return (
            <div key={group} className="group-block">
              <div className="group-label">{group}</div>
              <div className="sections-grid">
                {groupSections.map(secKey => (
                  <SectionCard
                    key={secKey}
                    secKey={secKey}
                    isActive={activeSection === secKey}
                    onClick={() => handleSectionClick(secKey)}
                  />
                ))}
              </div>

              {activeSection && SECTION_META[activeSection]?.group === group && (
                <div ref={expandedRef} className="expanded-wrapper">
                  <ExpandedSectionPanel
                    secKey={activeSection}
                    query={searchQuery}
                    studyMode={studyMode}
                    onCopy={speakWord}
                    view={currentView}
                    onClose={() => setActiveSection(null)}
                    onStudyCycle={cycleStudyMode}
                    onPlayGame={setGameWords}
                    dynamicSections={dynamicSections}   // 👈 SE INYECTA AQUÍ
                    dynamicThematic={dynamicThematic}   // 👈 SE INYECTA AQUÍ
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        className={`scroll-top ${showScrollTop ? 'vis' : ''}`}
        onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
        title="Back to top"
      >↑</button>

      <div className={`toast ${showToast ? 'show' : ''}`}>🔊 Pronouncing...</div>
      {/* ── MINIJUEGO MODAL ── */}
      {gameWords && (
        <MiniGameModal
          words={gameWords}
          isDark={isDark}
          onClose={() => setGameWords(null)}
        />
      )}
    </div>
  );
}